'use client';

import { useLayoutEffect, useRef, type ChangeEvent, type ForwardedRef } from 'react';

import {
  countCursorSignificantChars,
  cursorPosAfterSignificantChars,
  formatNumberInputValue,
  sanitizeNumberInput,
} from '../lib';
import type { NumberInputFormat } from './types';

type UseNumberInputParams = {
  value: string;
  onValueChange?: (value: string) => void;
  format: NumberInputFormat;
  decimalPlaces: number;
  ref: ForwardedRef<HTMLInputElement>;
};

export const useNumberInput = ({
  value,
  onValueChange,
  format,
  decimalPlaces,
  ref,
}: UseNumberInputParams) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const cursorSignificantIndexRef = useRef<number | null>(null);
  const displayValue = formatNumberInputValue(value, format);

  const setInputRefs = (node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  useLayoutEffect(() => {
    const significantIndex = cursorSignificantIndexRef.current;
    if (significantIndex == null || !inputRef.current) return;
    cursorSignificantIndexRef.current = null;
    const pos = cursorPosAfterSignificantChars(inputRef.current.value, significantIndex, format);
    inputRef.current.setSelectionRange(pos, pos);
  }, [displayValue, format]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextRaw = event.target.value;
    const selectionStart = event.target.selectionStart;
    const sanitized = sanitizeNumberInput(nextRaw, format, decimalPlaces);

    if (sanitized == null) return;

    if (selectionStart != null) {
      cursorSignificantIndexRef.current = countCursorSignificantChars(
        nextRaw.slice(0, selectionStart),
        format
      );
    } else {
      cursorSignificantIndexRef.current = null;
    }

    if (sanitized === value) {
      const significantIndex = cursorSignificantIndexRef.current;
      cursorSignificantIndexRef.current = null;
      if (significantIndex != null) {
        const pos = cursorPosAfterSignificantChars(displayValue, significantIndex, format);
        requestAnimationFrame(() => inputRef.current?.setSelectionRange(pos, pos));
      }
      return;
    }

    onValueChange?.(sanitized);
  };

  return {
    displayValue,
    setInputRefs,
    handleChange,
  };
};
